import { User, UserResponse, FieldsForEdit } from "@/types/User.interface";
import { makeAutoObservable, runInAction } from "mobx";
import { permissionsStore } from "./PermissionsStore";
import { WaitingUtils } from "./WaitingUtils";
import { universalServices } from "@/services/UniversalServices";
import { authStore } from "./AuthStore";
import { addNotification } from "@/utils/addNotification";
import { changesStore } from "./NotesStore";

export interface EditCurrentUser {
  id?: number;
  login?: string;
  role?: number;
  fio?: string;
  active?: boolean;
  lastLogin?: string | null;
  newPassword?: string;
  currentPassword?: string;
  other_users?: User[];
}

class UserStore {
  id: number | null = null;
  login: string = "";
  role: number | null = null;
  fio: string = "";
  active: boolean = false;
  isAuthenticated: boolean = false;
  isLoading: boolean = false;
  lastLogin: string | null = null;
  timer: NodeJS.Timeout | null = null;
  private _otherUsers: User[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  async setUser() {
    runInAction(() => {
      if (authStore.first) {
        this.isLoading = true;
      }
    });

    try {
      const result = await universalServices.getData<{ user: UserResponse }>("user", "get-current-user");
      console.log(result);

      runInAction(() => {
        if (result.success && result.data) {
          this.id = result.data.user.id;
          if (authStore.first) {
            changesStore.setData(result.data.user.id);
          }
          this.login = result.data.user.login;
          this.role = result.data.user.role;
          this.fio = result.data.user.fio;
          this.isAuthenticated = true;
          this.active = result.data.user.active;
          this.lastLogin = result.data.user.lastLogin;
          this._otherUsers = result.data.user.otherUsers || [];
        } else {
          this.lastLogin = null;
          this.id = null;
          this.login = "";
          this.role = null;
          this.fio = "";
          this.isAuthenticated = false;
          this.active = false;
          this._otherUsers = [];
        }
        this.isLoading = false;
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
      runInAction(() => {
        this.lastLogin = "";
        this.isLoading = false;
        this.isAuthenticated = false;
        this.id = null;
        this.login = "";
        this.role = null;
        this.fio = "";
        this.active = false;
        this._otherUsers = [];
      });
    }
  }

  setUserFromData(userData: EditCurrentUser | User[]) {
    runInAction(() => {
      if (Array.isArray(userData)) {
        this._otherUsers = userData;
      } else {
        this.id = userData.id || this.id;
        this.login = userData.login || this.login;
        this.role = userData.role || this.role;
        this.fio = userData.fio || this.fio;
        this.isAuthenticated = true;
        this.isLoading = false;
        this.active = userData.active || this.active;
        this.lastLogin = userData.lastLogin || this.lastLogin;
      }
    });
  }

  setActiveOtherUser = (data: EditCurrentUser) => {
    const user = this._otherUsers.find((u) => u.id === data.id);
    runInAction(() => {
      if (user) {
        if (data.active !== undefined) user.active = data.active;
      }
    });
  };

  // Очистка данных пользователя (при logout)
  clearUser = (): void => {
    runInAction(() => {
      this.id = null;
      this.login = "";
      this.role = null;
      this.fio = "";
      this.isAuthenticated = false;
      this.isLoading = false;
      this.active = false;
      this.lastLogin = null;
      this._otherUsers = [];
    });
  };

  clearTimer = (): void => {
    if (this.timer) {
      runInAction(() => {
        clearTimeout(this.timer!);
        this.timer = null;
      });
    }
  };

  get currentUser() {
    return {
      id: this.id,
      login: this.login,
      role: this.role,
      fio: this.fio,
      active: this.active,
      lastLogin: this.lastLogin,
      isAuthenticated: this.isAuthenticated,
    };
  }

  get otherUsers() {
    return this._otherUsers;
  }

  get roleName(): string {
    return permissionsStore.getPermissionsByRoleId(this.role!)?.roleName || "";
  }

  async toggleActiveUser(id: number): Promise<void> {
    const user = this._otherUsers.find((u) => u.id === id);
    if (!user) return;

    const newValue = !user.active;

    try {
      const res = await universalServices.getData<{ active: boolean; id: number }>("user", "toggle-active", {
        id,
        active: newValue,
      });

      if (res.success && res.data) {
        this.setActiveOtherUser({
          id: res.data.id,
          active: res.data.active,
        });
        addNotification({
          message: `Пользователь ${res.data.active ? "активирован" : "деактивирован"}`,
          type: "success",
        });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
    }
  }

  get isAdmin(): boolean {
    return permissionsStore.getPermissionsByRoleId(this.role!)?.roleName === "Administrator";
  }
}

export const userStore = new UserStore();

export class UserUtils extends WaitingUtils {
  constructor() {
    super(() => {
      const token = localStorage.getItem("api_token");
      if (token) {
        return authStore.checkAuth();
      }
    });
  }

  async setNewDataAutenticatedUser(newData: FieldsForEdit, action: string = "edit-user"): Promise<void> {
    try {
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      const editUserFn = async (data: FieldsForEdit): Promise<any> => {
        return await universalServices.getData<FieldsForEdit>("user", action, data);
      };

      this.setData<FieldsForEdit, (data: FieldsForEdit) => Promise<FieldsForEdit>>(newData, editUserFn);
    } catch (e) {
      const message = e instanceof Error ? e.message : typeof e === "string" ? e : "Unknown error";
      addNotification({ message: message, type: "error" });
    }
  }
}

export const userUtils = new UserUtils();
